import z from 'zod'
import {
  nullSafeBoolean,
  nullSafeNumber,
  nullSafeOptionalStringArray,
  nullSafeStringArray,
} from '../shared/schema'
import { SessionMuting, RoomStateVideoMethod } from '../../types'

const sessionMutingSchema = z.nativeEnum(SessionMuting)
const videoMethodSchema = z.nativeEnum(RoomStateVideoMethod)

const automaticRoomNameValuesSchema = z
  .object({
    adjectives: nullSafeStringArray([]),
    nouns: z.record(z.string(), z.string()),
  })
  .optional()

export const schema = z.object({
  adminRoleManagementEnabled: nullSafeBoolean(false),
  automaticRoomNameValues: z.preprocess((val) => {
    if (val) {
      const parsed = automaticRoomNameValuesSchema.safeParse(val)
      // if it's correctly formatted, use it
      // in any other case, undefined
      if (parsed.success) return parsed.data
    }

    return undefined
  }, automaticRoomNameValuesSchema),
  animationMethod: nullSafeBoolean(true),
  generateGoogleSlides: nullSafeBoolean(false),
  generateSessionSummary: nullSafeBoolean(true),
  rubricTestDesiredStdDeviation: nullSafeNumber(1.5),
  rubricTestMeanEpsilon: nullSafeNumber(0.9),
  sendInstructorEmails: nullSafeBoolean(false),
  sessionMuting: z.preprocess(
    (val) => val ?? SessionMuting.none,
    sessionMutingSchema
  ),
  sessionResultsGroupBalanceCoefficient: nullSafeNumber(0.5),
  sessionResultsShowAllConversationTooltips: nullSafeBoolean(true),
  showRubricArgumentEventMatchMinimum: nullSafeNumber(1), // float value
  useStrictPublishRequirements: nullSafeBoolean(false),
  videoMethod: z.preprocess(
    (val) => val ?? RoomStateVideoMethod.broadcast,
    videoMethodSchema
  ),
  showOrganizationsUI: nullSafeBoolean(false),
  audioSilenceDetectionEnabled: nullSafeBoolean(false),
  transcriptionLanguages: nullSafeOptionalStringArray(),
  transcriptProblemWaitDurationMS: nullSafeNumber(0), // in ms
  verifyInvitation: nullSafeBoolean(false),
  showAssignmentCSVResultsInMinutesAndSortingAsPercent: nullSafeBoolean(false),
  demoVideoMethod: z.preprocess((val) => {
    // this is protection against bad data - if it's streaming,
    // use it, otherwise default to broadcast
    if (val === RoomStateVideoMethod.streaming) return val
    return RoomStateVideoMethod.broadcast
  }, videoMethodSchema),
  // timeout in seconds, cannot be negative
  soloBotNudgeButtonTimeout: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Background-blur intensity passed to LiveKit's BackgroundProcessor.
  // 0 disables the feature entirely (no UI, no keyboard shortcut).
  backgroundBlurRadius: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Kill-switch for Safari only. When true, blur is force-disabled and the
  // UI is hidden for Safari users, regardless of `backgroundBlurRadius`.
  disableBackgroundBlurInSafari: nullSafeBoolean(false),
  hideSlideDeckPrice: nullSafeBoolean(false),
  useNextbookImportForm: nullSafeBoolean(false),
  enhancedSupportLinkParams: nullSafeBoolean(true),
  useSharedGoogleDocSlide: nullSafeBoolean(false),
  serviceWorkerKillSwitch: nullSafeBoolean(false),
  useNewGradingApi: nullSafeBoolean(false),
  useApiOnProfilePage: nullSafeBoolean(false),
  instructorsWhoCanViewTranscripts: nullSafeOptionalStringArray(),
  useV2SlideDeckCatalog: nullSafeBoolean(false),
  // Gates the customizable grading controls (per-rubric weights + score
  // mapping). When false, the grading dialog hides the scoring section and the
  // "customize weights" link and reverts to the legacy (narrower) width.
  customRubricsEnabled: nullSafeBoolean(false),
  // When true, custom-rubric saves go to the AdonisJS server endpoint
  // (POST /api/v1/assignments/rubrics) instead of the
  // `createOrUpdateAssignmentRubrics` cloud function. Default keeps the
  // cloud function.
  useApiForCustomRubrics: nullSafeBoolean(false),
  // When true, the A/V-room caption overlay consumes the WebVTT file for each
  // slide video instead of the legacy slide_caption Firestore subcollection.
  consumeVttCaptions: nullSafeBoolean(false),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsFeatureFlags = z.infer<typeof schema>
